import { Fragment, useEffect, useRef, useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { EssayStructureDiagram } from "./EssayStructureDiagram";
import { derivePartExplanations } from "../lib/bigPictureSynthesis";
import { deriveArgumentSkeleton, PART_TITLE, title as shortTitle, needLabel, paragraphRefs as refs, sentenceNumbers as words } from "../lib/argumentSkeleton";
import { conceptLabel, bodyRows, PART_NAME as NAME } from "../lib/wholeRows";
import { getTestMode, TEST_MODE_EVENT } from "../lib/testMode";

export { conceptLabel, bodyRows };

// THE EMERGING WHOLE — a clean structural map of the essay, not a diagnostic report. LEFT: what Introduction / Body / Conclusion are doing and
// what the student has built (interpretation), then Next (Problem · Why it matters · What to do). RIGHT: the structural map in the student's own
// sentences. A DERIVED projection from the authoritative state; read-only. Labels stay evidence-bound: a concept is named only when the appraiser's
// sentence quotes exactly one term; a combined paragraph reads "two ideas still together", never a fabricated pair. TWS proposes (tentative copy) ·
// the student owns by acting (ordinary copy). The diagnostic / provenance DETAILS (requirement standings, unit notes, relation readings, unplaced
// material) stay in the projection for the controller, tests and developer inspection; they are shown ONLY in Test Mode, never to a student.
const PARTS = ["introduction", "body", "conclusion"];
// TYPOGRAPHY: one family (the app sans), two weights (medium for section labels and the current target · regular), two sizes (13px body · 12px
// disclosures), two tones (stone-800 · stone-500); no italics, no capitals, no letterspacing — spacing does the work.
// TBP-F6 TYPOGRAPHY — four treatments only: SECTION (section heading) · H (card / major-branch heading) · PRIMARY (body text) · QUIET (status/support)
// TBP-F7: body 14 · headings 16 (weight, not size, carries hierarchy) · status 13 muted — no large jumps anywhere in the Big Picture
const H = "text-[14px] font-semibold text-stone-900";
const PRIMARY = "text-[14px] leading-relaxed text-stone-800";
const QUIET = "text-[13px] leading-relaxed text-stone-500";

const stripPart = (s) => (s || "").replace(/^In the (Introduction|Body|Conclusion): /, "");   // the part is visible in the Big Picture itself
// (the persistent Big Picture bar was retired: the workspace is Essay + Coach; Big Picture is a TOOL — Tools ▾ → Big Picture — or opened by TWS under Level-2 orientation)

function useTestMode() {
  const [on, setOn] = useState(getTestMode);
  useEffect(() => { const f = () => setOn(getTestMode()); window.addEventListener(TEST_MODE_EVENT, f); return () => window.removeEventListener(TEST_MODE_EVENT, f); }, []);
  return on;
}

// LAYOUT — two columns that grow naturally (no fixed heights): LEFT ≈ 40% = the compositional explanation (Introduction · Body · Conclusion) with NEXT
// (+ TWS's brief orienting words when TWS opened it) at its foot; RIGHT ≈ 60% = the content diagram, given the room its wrapped claims need. No
// full-width block beneath the columns; on narrow screens the left column stacks first (… Next), then the diagram.
// ── THE STUDENT-FACING BIG PICTURE: a quick visual picture of the essay ──────────────────────────────────────────────────────────────────
// 1. YOUR MAIN IDEA (one distinct card) · 2. HOW YOUR ESSAY IS TAKING SHAPE (three connected blocks: Introduction → Body → Conclusion; stacked
// with ↓ on narrow screens; each block carries only its few short labels and a quiet status that never relies on colour alone) · 3. DETAILS
// (prose v1). Next, the coach's orientation line, the structural tree and the analytic graph live in Internal diagnostics (test mode) only.
const CARD = "rounded-2xl border bg-white";
const SECTION = "text-[16px] font-semibold leading-snug text-stone-900";
const STATUS_TEXT = { not_yet: "", later: "", written: "" };

// status of a block, for the eye AND for words: established (solid mark) · working here (accent mark) · not developed yet (hollow mark, lighter)
const blockState = (part) => (part.current ? "working" : part.nodes.length || part.status === "written" || part.unplaced ? "established" : "empty");
const MARK = { established: "bg-stone-800", working: "bg-[#8C3A2A]", empty: "border border-stone-400" };
const STATE_WORD = { established: "", working: "Working here", empty: "" };

const developing = (part, thesis, bodyLabels) => {
  const t = (thesis || "").replace(/[.]+$/, "");
  if (part.key === "introduction") return t ? `enough context for the reader to understand why this question matters before they reach your thesis — “${t}.”` : "enough context for the reader to understand why this question matters, and then your thesis — the organizing idea of the essay.";
  if (part.key === "conclusion") return `bring together what the essay has established${bodyLabels.length ? ` — ${bodyLabels.slice(0, 4).join("; ")} —` : ""} and say why that matters for your thesis.`;
  return t ? `what the essay must establish for your thesis to hold — the parts that make the case for “${t}.”` : "what the essay must establish for your thesis to hold.";
};

export function SkeletonPart({ part, needs = "", thesis = "", provisionalThesis = false, bodyLabels = [], hereLabel = "Working here" }) {
  const state = blockState(part);
  const named = part.nodes.length > 0;
  const contrastIds = part.contrast ? [part.contrast.a, part.contrast.b] : [];
  const plural = (n, w) => `${n} ${w}${n === 1 ? "" : "s"}`;
  const statusLine = part.key === "body" && part.unplaced && !named ? `${plural(part.unplaced, "idea")} waiting to be organized`
    : STATUS_TEXT[part.status] || (part.key === "introduction" ? "Sets up your thesis" : part.more ? `${plural(part.more, "paragraph")} written` : "Written");
  return (
    <div data-testid={`bp-part-${part.key}`} data-current={part.current ? "1" : "0"} data-status={part.status} data-state={state}
      className={`${CARD} flex flex-col px-5 py-4 ${state === "working" ? "border-[#8C3A2A] shadow-[0_1px_0_#8C3A2A]" : state === "empty" ? "border-dashed border-stone-300" : "border-stone-300"}`}>
      <div className="flex items-center gap-2">
        <span aria-hidden="true" className={`inline-block h-2.5 w-2.5 rounded-full ${MARK[state]}`} />
        <span data-testid={`bp-part-title-${part.key}`} className={`text-[13px] font-semibold uppercase tracking-[0.1em] ${state === "empty" ? "text-stone-400" : "text-stone-800"}`}>{PART_TITLE[part.key]}</span>
        {state === "working" ? <span data-testid="bp-current-marker" className="ml-auto whitespace-nowrap text-[12px] text-[#8C3A2A]">{hereLabel}</span> : null}
      </div>
      <ul className="mt-3 space-y-2">
        {named && part.statusLine ? <li data-testid={`bp-status-${part.key}`} className={`pb-1 ${QUIET}`}>{words(part.statusLine)}</li> : null}
        {part.nodes.map((n, i) => (
          <li key={n.id} data-testid={`bp-node-${n.id}`} data-current={n.current ? "1" : "0"} className="leading-snug">
            {part.contrast && i > 0 && contrastIds.includes(n.id) && contrastIds.includes(part.nodes[i - 1].id)
              ? <div data-testid="bp-contrast" className="mb-1 -mt-1 text-[12px] text-stone-400">{part.contrast.inferred ? "in contrast (as TWS reads it)" : "in contrast"}</div> : null}
            <span className={`${PRIMARY} ${n.current ? "font-medium text-[#8C3A2A]" : ""}`}>{n.label}</span>
            {n.provisional ? <span data-testid={`bp-provisional-${n.id}`} className={`block ${QUIET}`}>TWS currently places this here — you can move it, change its purpose, or set it aside.</span> : null}
            {n.sub && n.sub.length ? <span data-testid={`bp-sub-${n.id}`} className={`block pl-3 ${QUIET}`}>{n.sub.map((x) => `\u2192 ${x}`).join(" ")}</span> : null}
          </li>
        ))}
        {!named ? <li data-testid={`bp-status-${part.key}`} className={`${state === "empty" ? QUIET : PRIMARY}`}>{state === "empty" ? <><span className="text-[#8C3A2A]">What still needs developing: </span>{developing(part, thesis, bodyLabels)}</> : words(part.statusLine || statusLine)}</li> : null}
        {named && part.more > 0 ? <li data-testid={`bp-more-${part.key}`} className={`${QUIET}`}>+ {plural(part.more, "more paragraph")}</li> : null}
        {!named && part.key === "body" && part.more > 0 ? <li data-testid="bp-provisional-more" className={`${QUIET}`}>{words(`${plural(part.more, "idea")} placed here for now`)} — TWS currently places them in the Body; you can move them, give them a purpose, or set them aside.</li> : null}
        {named && part.unplaced ? <li data-testid="bp-unplaced" className={`${QUIET}`}>{words(`${plural(part.unplaced, "idea")} waiting to be organized`)}</li> : null}
      </ul>
      {part.key === "introduction" ? (
        <div data-testid="bp-intro-thesis" data-tour="big-picture-left" className="mt-3 border-l-2 border-[#8C3A2A] pl-3">
          <p className={`${QUIET}`}>Context / setup</p>
          <p className="mt-1 font-mono uppercase tracking-[0.12em] text-[11px] text-[#8C3A2A]">Thesis / organizing idea{provisionalThesis && thesis ? <span className="normal-case tracking-normal text-stone-400"> (as TWS reads it)</span> : null}</p>
          <p data-testid="ew-main-idea-text" className={`text-[14px] leading-snug ${thesis ? "font-medium text-stone-900" : "text-stone-400"}`}>{thesis || "Not stated yet — this is the claim the whole essay will organize around."}</p>
        </div>
      ) : null}
      {/* the SAME reading Details gives under "Still needs work" — the card and Details never disagree */}
      {needs ? <p data-testid={`bp-needs-${part.key}`} className={`mt-2 ${QUIET}`}><span className="text-[#8C3A2A]">What still needs work: </span>{words(needs)}</p> : null}
    </div>
  );
}

export const Arrow = () => (
  <div aria-hidden="true" className="flex items-center justify-center self-center md:self-start md:pt-4 text-stone-300">
    <span className="hidden md:inline text-[16px]">{"\u2192"}</span>
    <span className="md:hidden text-[16px]">{"\u2193"}</span>
  </div>
);

// TBP-F8 — FINAL STUDENT CONTRACT. Ordinary mode renders ONLY: main idea · how the essay is taking shape (Introduction · Body · Conclusion) ·
// Details (prose v1). No Next card, no coach orientation line, no structural tree, no analytic graph: the coach is the sole instructional voice; the
// Big Picture is a descriptive structural view. `whole.next`, `student_view.next`, the Episode Target and the orientation text stay in the data and
// render ONLY inside "Internal diagnostics (test mode)" — one collapsed disclosure, visually separated from the student-facing content.
export function EmergingWhole({ whole, primary = false, inline = false, orientation = "" }) {
  const [details, setDetails] = useState(false);
  const [studentDetails, setStudentDetails] = useState(false);
  const testMode = useTestMode();
  if (!whole || !whole.essay) return null;
  const { essay } = whole;
  const sk = deriveArgumentSkeleton(whole);
  return (
    <section data-testid="emerging-whole" data-open="1" data-primary={primary ? "1" : "0"} data-signature={whole.signature} data-focus={essay.focus || ""} data-nodes={sk.nodeCount} aria-label="Big Picture"
      className={inline ? "px-1 pb-2 text-[14px] leading-relaxed text-stone-800" : "px-5 py-6 text-[14px] leading-relaxed text-stone-800 sm:px-8 sm:py-10"}>
      <div data-testid="emerging-whole-body" className="mx-auto max-w-3xl space-y-8">
        {/* 1 · YOUR MAIN IDEA — one distinct, restrained card */}
        {/* The thesis is rendered INSIDE the Introduction card (same canonical value) — no separate global card above the structure. */}

        {/* 2 · HOW YOUR ESSAY IS TAKING SHAPE — three connected blocks */}
        <div data-testid="ew-skeleton" data-tour="big-picture-diagram">
          <h2 className={SECTION}>How your essay is taking shape</h2>
          <div className="mt-4 grid grid-cols-1 items-start gap-3 md:grid-cols-[1fr_auto_1fr_auto_1fr]">
            {sk.parts.map((part, i) => (
              <Fragment key={part.key}>
                {i > 0 ? <Arrow /> : null}
                <SkeletonPart part={part} needs={whole.student_view ? detailText(whole.student_view, part.key, "needs") : ""} thesis={sk.mainIdea.text} provisionalThesis={sk.mainIdea.ownership !== "student"}
                  bodyLabels={sk.parts[1].nodes.filter((n) => !n.provisional).map((n) => n.label)} />
              </Fragment>
            ))}
          </div>
        </div>

        {/* 3 · DETAILS — prose v1 only (What it is doing · Working · Still needs work) */}
        {whole.student_view ? (
          <div className="pt-1" data-testid="ew-student-details">
            <Disclosure id="student-details" open={studentDetails} onToggle={() => setStudentDetails((o) => !o)} label="Details">
              <StudentDetails sv={whole.student_view} />
            </Disclosure>
          </div>
        ) : null}
        {/* INTERNAL DIAGNOSTICS — test mode only, collapsed by default, separated from the student view (never mixed into the cards above) */}
        {testMode ? (
          <div data-testid="ew-developer" className="border-t border-dashed border-stone-300 pt-5">
            <Disclosure id="details" open={details} onToggle={() => setDetails((o) => !o)} label="Internal diagnostics (test mode)">
              <InternalDiagnostics whole={whole} sk={sk} orientation={orientation} />
            </Disclosure>
          </div>
        ) : null}
      </div>
    </section>
  );
}

// INTERNAL DIAGNOSTICS (developer / test inspection only): next · Episode Target · the coach's orientation line · structural tree · provenance /
// evidence. Read from the same projection; renders nothing a student sees in ordinary mode; never a second student-style Details.
const DIAG_H = "text-[12px] font-semibold uppercase tracking-[0.1em] text-stone-500";
function InternalDiagnostics({ whole, sk, orientation }) {
  const { next } = whole;
  const sv = whole.student_view || null;
  const pr = sv && sv.prose ? sv.prose : null;
  const dx = next && next.diagnosis && next.diagnosis.problem ? next.diagnosis : null;
  const nextText = next && next.summary ? (next.requirement_id ? stripPart(next.summary) : next.summary) : "";
  const words = sv && (sv.relations || []).length ? Array.from(new Set(sv.relations.map((r) => r.word))).join(", ") : "";
  return (
    <div data-testid="ew-diagnostics" className="space-y-7">
      <div data-testid="ew-next" data-tour="big-picture-next" data-requirement={next && next.requirement_id ? next.requirement_id : ""} data-authority={dx ? dx.authority || "" : ""} data-from={next ? next.from : "none"}>
        <h3 className={DIAG_H}>Next / Episode Target</h3>
        {sk.next ? (
          <>
            <p data-testid="ew-next-action" className={`mt-2 ${PRIMARY}`}>{sk.next.action}</p>
            {sk.next.why ? <><p className={`mt-2 ${H}`}>Why</p><p data-testid="ew-next-why" className={`mt-0.5 ${PRIMARY}`}>{sk.next.why}</p></> : null}
          </>
        ) : <p className={`mt-2 ${QUIET}`}>No open target.</p>}
        {sv && sv.next ? <p data-testid="ew-sd-next" className={`mt-2 ${QUIET}`}><span className="font-medium">student_view.next: </span>{pr ? pr.next.action : sv.next.action}</p> : null}
        {dx ? (
          <dl data-testid="ew-next-diagnosis" className={`mt-3 space-y-1.5 ${PRIMARY}`}>
            <div><dt className="font-medium">Problem</dt><dd data-testid="ew-next-problem">{dx.problem}</dd></div>
            {dx.why ? <div><dt className="font-medium">Why it matters</dt><dd data-testid="ew-next-why-full">{dx.why}</dd></div> : null}
            <div><dt className="font-medium">What to do</dt><dd data-testid="ew-next-what">{dx.what}</dd></div>
          </dl>
        ) : nextText ? <p data-testid="ew-next-text" data-requirement={next && next.requirement_id ? next.requirement_id : ""} className={`mt-3 ${PRIMARY}`}>{nextText}</p> : null}
        {orientation ? <p data-testid="ew-orientation" className={`mt-3 ${QUIET}`}><span className="font-medium">Coach orientation line: </span>{orientation}</p> : null}
      </div>
      {sv ? <div data-testid="ew-structural-view"><h3 className={`mb-2 ${DIAG_H}`}>Structural tree</h3><StudentEssayDiagram sv={sv} /></div> : null}
      <dl data-testid="ew-provenance" className={`grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 ${QUIET}`}>
        <dt className="col-span-2"><span className={DIAG_H}>Provenance / evidence</span></dt>
        <dt>signature</dt><dd data-testid="ew-prov-signature" className="break-all">{whole.signature || "—"}</dd>
        <dt>focus</dt><dd>{whole.essay.focus || "—"}</dd>
        <dt>next.from</dt><dd>{next ? next.from || "—" : "none"}</dd>
        <dt>requirement</dt><dd>{next && next.requirement_id ? next.requirement_id : "—"}</dd>
        <dt>relations</dt><dd>{(whole.relations || []).length} in the projection{sv ? ` · ${(sv.relations || []).length} credited in student_view${words ? ` (${words})` : ""}` : ""}</dd>
        {sv ? <><dt>current</dt><dd>{sv.current || "—"}</dd><dt>main idea wording</dt><dd>{sv.main_idea ? sv.main_idea.wording || "—" : "—"}</dd></> : null}
        <dt>flags</dt><dd>{[whole.early ? "early" : "", whole.stale ? "stale" : ""].filter(Boolean).join(", ") || "—"}</dd>
      </dl>
    </div>
  );
}

// BIG PICTURE MODE — ONE intellectual object at a time: the whole temporarily REPLACES the Essay/Structure workspace AND the side dialogue panel
// (the dialogue belongs to the working workspace; this is the whole-orientation workspace). When TWS opened it (level 2), the coach's brief
// orienting words are shown beside Next — never the conversation transcript. `onReturn` is the workspace's ONE `returnToEssay` action (the header
// Return control invokes the same function). View state only: no target, controller, provenance or history writes.
export function BigPictureWorkspace({ whole, from, onReturn, reason, coach }) {
  return (
    <div id="big-picture-workspace" data-testid="big-picture-expanded" data-from={from || "essay"} data-reason={reason || ""} className="pt-1">
      {reason ? <p data-testid="big-picture-reason" className="mb-3 text-[12px] text-stone-500">TWS opened the Big Picture: {reason}</p> : null}
      {whole && whole.essay ? <EmergingWhole whole={whole} primary inline orientation={coach || ""} /> : <p className="text-[12px] text-stone-500">TWS is reading your writing…</p>}
      <button type="button" data-testid="big-picture-return" data-tour="big-picture-return" onClick={onReturn}
        className="mt-3 text-[13px] font-medium text-stone-800 hover:text-[#8C3A2A] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#8C3A2A]">{"\u2190"} Return to Essay</button>
    </div>
  );
}

// TBP-F2 — STUDENT DETAILS: per part, WHAT IT IS DOING · WORKING · STILL NEEDS WORK, in short prose, plus the simplified essay diagram — all read from
// the ONE reconciled current-essay model. Absent and present-but-weak are distinct; TWS readings say so; no internal ids, no analytic graph.
const D_PARTS = ["introduction", "body", "conclusion"];
// a paragraph is identified by its opening words, never by a cut quotation used as a name
const plain = (t) => refs((t || "").replace(/^TWS reads a reader need here: /, ""));
function needsWork(sv, k, label) {
  const p = sv.parts[k], why = (sv.next && sv.next.why) || "";
  if (!p.needs_work) return "";
  if (why && p.needs_work.startsWith(why.slice(0, 60))) return `Clarify: ${label.toLowerCase()}.`;         // the full Why appears ONLY in Next
  if (k === "conclusion" && /^TWS reads a reader need here/.test(p.needs_work) && /\b(link|connection|entail|gap)\b/i.test(p.needs_work))
    return "The connection between these ideas is not clear enough yet.";
  return plain(p.needs_work);
}
// ONE source for the student-facing part text (cards AND Details): the prose sentence when the communication layer produced one; model text otherwise
function detailText(sv, k, f) {
  const pr = sv.prose || null;
  const key = pr ? (f === "doing" ? "summary" : f === "needs" ? "needs_work" : f) : f;
  if (pr && pr[k] && typeof pr[k][key] === "string") return words(pr[k][key]);
  const p = sv.parts[k];
  return words(f === "doing" ? p.doing : f === "working" ? p.working : needsWork(sv, k, needLabel(sv.next)));
}
function StudentDetails({ sv }) {
  const pr = sv.prose || null;
  const text = (k, f) => detailText(sv, k, f);
  return (
    <div data-testid="ew-student-rich" className="mb-4 max-w-3xl">
      <div className="space-y-5 break-words">
        {D_PARTS.map((k) => {
          const p = sv.parts[k];
          return (
            <div key={k} data-testid={`ew-sd-${k}`} data-status={p.status}>
              <h3 className={`${H} uppercase tracking-[0.1em] text-[13px]`}>{NAME[k]}</h3>
              <p data-testid={`ew-sd-${k}-doing`} className={`mt-1 ${PRIMARY}`}><span className="font-medium">What it is doing: </span>{text(k, "doing")}</p>
              {k === "body" && (p.major_branches || []).length ? (
                <ul data-testid="ew-sd-body-branches" className="mt-2 space-y-2">
                  {p.major_branches.map((b, i) => (
                    <li key={b.id} data-testid={`ew-sd-branch-${i}`}>
                      <div className={H}>{i + 1}. {pr && pr.body.branches[i] ? pr.body.branches[i].heading : b.label}{b.provenance === "tws_reading" ? <span className={` ${QUIET} font-normal`}> (as TWS currently sees it)</span> : null}</div>
                      {pr && pr.body.branches[i] ? <p className={`mt-0.5 ${PRIMARY}`}>{words(pr.body.branches[i].sentence)}</p> : <p className={`mt-0.5 ${QUIET}`}>{words(b.relation_to_main_idea)}</p>}
                      <ul className={`mt-1 pl-4 ${QUIET}`}>
                        {pr && pr.body.branches[i]
                          ? pr.body.branches[i].moves.map((m, j) => <li key={j}>{"\u2022 "}{m}</li>)
                          : (b.child_move_ids || []).map((sid) => (p.moves || []).find((st) => st.id === sid)).filter(Boolean).map((st) => (
                            <li key={st.id}>{"\u2022 "}{shortTitle(st.label)}{(st.sub || []).map((x) => ` \u2192 ${x.via ? x.via + ": " : ""}${shortTitle(x.label)}`).join("")}</li>
                          ))}
                      </ul>
                    </li>
                  ))}
                </ul>
              ) : null}
              {text(k, "working") ? <p data-testid={`ew-sd-${k}-working`} className={`mt-1 ${PRIMARY}`}><span className="font-medium">Working: </span>{text(k, "working")}</p> : null}
              {text(k, "needs") ? <p data-testid={`ew-sd-${k}-needs`} className={`mt-1 ${PRIMARY}`}><span className="font-medium">Still needs work: </span>{text(k, "needs")}</p>
                : <p data-testid={`ew-sd-${k}-needs`} className={`mt-1 ${QUIET}`}>TWS does not currently read a structural problem here.</p>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// the simplified student diagram: MAIN IDEA → Introduction · Body moves grouped under major branches (inner connectors only where credited) · Conclusion — 6–10 nodes
function StudentEssayDiagram({ sv }) {
  const d = sv.diagram;
  const pr = sv.prose || null;
  if (pr) return <PrimaryDiagram sv={sv} pr={pr} />;
  const line = (glyph, text, cls = "text-stone-800", tid) => (
    <li key={tid} data-testid={tid} className="flex items-start"><span aria-hidden className="shrink-0 select-none whitespace-pre text-stone-400">{glyph}</span><span className={`min-w-0 flex-1 break-words ${cls}`}>{text}</span></li>
  );
  const nodes = [];
  nodes.push(line("", <><span className={`${QUIET} mr-1 uppercase tracking-[0.12em]`}>Main idea </span>{d.main.label}{d.main.provisional ? <span className={` ${QUIET}`}> (as TWS reads it)</span> : null}</>, "text-stone-900 font-medium", "sed-main"));
  nodes.push(line("\u251c\u2500\u2500 ", <><span className="font-medium">Introduction</span>{d.introduction.status === "absent" ? <span className={QUIET}> \u2014 not written yet</span> : d.introduction.label ? ` \u2014 ${shortTitle(d.introduction.label)}` : ""}</>, "text-stone-800", "sed-intro"));
  if (d.introduction.working_on) nodes.push(line("\u2502   \u2514\u2500\u2500 ", `working on: ${needLabel(sv.next).toLowerCase()}`, QUIET, "sed-intro-working"));
  nodes.push(line("\u251c\u2500\u2500 ", <span className="font-medium">Body</span>, "text-stone-800", "sed-body"));
  const moveLines = (s, pre, last) => {
    nodes.push(line(`${pre}${last ? "\u2514" : "\u251c"}\u2500\u2500 `, shortTitle(s.label), "text-stone-800", `sed-move-${s.id}`));
    (s.children || []).forEach((c, j) => nodes.push(line(`${pre}${last ? " " : "\u2502"}   ${j === s.children.length - 1 ? "\u2514" : "\u251c"}\u2500\u2500 `, <>{c.via ? <span className={QUIET}>{c.via} → </span> : null}{shortTitle(c.label)}</>, "text-stone-700", `sed-sub-${c.id}`)));
  };
  const branches = d.branches || [];
  if (branches.length) {
    branches.forEach((b, i) => {                               // ESSAY → MAJOR BRANCH → paragraph-level moves (hierarchy visible)
      const last = i === branches.length - 1;
      nodes.push(line(`\u2502   ${last ? "\u2514" : "\u251c"}\u2500\u2500 `, <><span className="font-medium">{i + 1}. {b.label}</span>{b.provenance === "tws_reading" ? <span className={QUIET}> (as TWS sees it)</span> : null}</>, "text-stone-900", `sed-branch-${i}`));
      const kids = (b.child_move_ids || []).map((sid) => d.body.find((s) => s.id === sid)).filter(Boolean);
      kids.forEach((s, j) => moveLines(s, `\u2502   ${last ? " " : "\u2502"}   `, j === kids.length - 1));
    });
  } else {
    d.body.forEach((s, i) => moveLines(s, "\u2502   ", i === d.body.length - 1));
  }
  if (!d.body.length) nodes.push(line("\u2502   \u2514\u2500\u2500 ", "nothing in the Body yet", QUIET, "sed-body-none"));
  nodes.push(line("\u2514\u2500\u2500 ", <><span className="font-medium">Conclusion</span>{d.conclusion.status === "absent" ? <span className={QUIET}> \u2014 not written yet</span> : ` \u2014 ${shortTitle(d.conclusion.label)}`}{d.conclusion.note ? <span className={QUIET}> [{d.conclusion.note}]</span> : null}</>, "text-stone-800", "sed-concl"));
  return <ul data-testid="student-essay-diagram" data-nodes={nodes.length} className={`space-y-1 ${PRIMARY}`}>{nodes}</ul>;
}

// TBP-F6 PRIMARY STRUCTURAL DIAGRAM (middle level, ~7–10 items): every node reads as a complete proposition or a named part
function PrimaryDiagram({ sv, pr }) {
  const d = sv.diagram;
  const row = (tid, glyph, head, body, cls = PRIMARY) => (
    <li key={tid} data-testid={tid} className="flex items-start">
      <span aria-hidden className="shrink-0 select-none whitespace-pre text-stone-400">{glyph}</span>
      <span className={`min-w-0 flex-1 break-words ${cls}`}>{head ? <span className={H}>{head} </span> : null}{body}</span>
    </li>
  );
  const items = [];
  items.push(row("sed-main", "", "Main idea:", <>{d.main.label}{d.main.provisional ? <span className={QUIET}> (as TWS reads it)</span> : null}</>));
  items.push(row("sed-intro", "\u251c\u2500\u2500 ", "Introduction.", d.introduction.status === "absent" ? "The essay has no introduction yet." : pr.diagram.introduction));
  items.push(row("sed-body", "\u251c\u2500\u2500 ", "Body.", words(pr.body.status)));
  pr.body.branches.forEach((b, i) => {
    const last = i === pr.body.branches.length - 1;
    items.push(row(`sed-branch-${i}`, `\u2502   ${last ? "\u2514" : "\u251c"}\u2500\u2500 `, `${i + 1}. ${b.heading}.`, b.sentence));
    b.moves.forEach((m, j) => items.push(row(`sed-move-${i}-${j}`, `\u2502   ${last ? " " : "\u2502"}   ${j === b.moves.length - 1 ? "\u2514" : "\u251c"}\u2500\u2500 `, "", m, QUIET)));
  });
  items.push(row("sed-concl", "\u2514\u2500\u2500 ", "Conclusion.", d.conclusion.status === "absent" ? "The essay has no conclusion yet." : pr.diagram.conclusion));
  const edges = (sv.relations || []).length;
  return (
    <div>
      <ul data-testid="student-essay-diagram" data-nodes={items.length} className="space-y-1.5">{items}</ul>
      {edges ? <p className={`mt-2 ${QUIET}`}>Connections you have made between paragraphs: {Array.from(new Set(sv.relations.map((r) => r.word))).join(", ")}.</p> : null}
    </div>
  );
}

// THE RICH PROJECTION — NOT rendered in the Big Picture (it read as a second Details). Exported for tests / developer inspection of
// `derivePartExplanations` + `EssayStructureDiagram` only; the diagnosis (Problem · Why it matters · What to do) lives in the diagnostics Next block.
export function BigPictureRich({ whole }) {
  const ex = derivePartExplanations(whole);
  return (
    <div data-testid="ew-rich" data-current={ex.currentId || ""} className="mb-4 grid grid-cols-1 items-start gap-x-10 gap-y-6 md:grid-cols-[2fr_3fr]">
      <div data-testid="ew-prose" className="space-y-5 break-words">
        {PARTS.map((k) => <Prose key={k} id={k} heading={NAME[k]} part={ex[k]} />)}
      </div>
      <EssayStructureDiagram whole={whole} />
    </div>
  );
}

function Prose({ id, heading, part }) {
  return (
    <div data-testid={`ew-${id}`}>
      <h3 className={H}>{heading}</h3>
      <p data-testid={`ew-${id}-function`} className={`mt-1 ${QUIET}`}>What it does: {part.function}</p>
      <p data-testid={`ew-${id}-now`} className={`mt-1 ${PRIMARY}`}>Your essay now: {part.now.join(" ")}</p>
    </div>
  );
}

function Disclosure({ id, open, onToggle, label, children }) {
  return (
    <div data-testid={`ew-${id}`} data-open={open ? "1" : "0"}>
      <button type="button" data-testid={`ew-${id}-toggle`} aria-expanded={open} aria-controls={`ew-${id}-panel`} onClick={onToggle}
        className="flex items-center gap-1 text-[13px] font-medium text-stone-600 hover:text-stone-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#8C3A2A]">
        {label}{open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
      </button>
      {open ? <div id={`ew-${id}-panel`} data-testid={`ew-${id}-panel`} className={`mt-2 pl-3 break-words ${PRIMARY}`}>{children}</div> : null}
    </div>
  );
}


// the fetch + collapsed state, kept out of ThinkingWorkspace: re-derives whenever the record changes (any applied `ot`), never writes.
// WHOLE ORIENTATION (presentation signal from the turn): level 0 → nothing · level 1 → nothing here (the coach's words carry the whole-reference) ·
// level 2 → open ONCE for that orientation key; the student's close (Return / the bar) reports the key as dismissed (`onDismiss`) so the same
// orientation is never forced open again. Student-initiated opening is view state only and reports nothing.
export function useEmergingWhole(sessionId, ot, enabled, fetcher, onDismiss) {
  const [whole, setWhole] = useState(null);
  const key = `compass_whole_open|${sessionId}`;
  const [open, setOpen] = useState(() => { try { return sessionStorage.getItem(key) === "1"; } catch { return false; } });   // collapsed by default
  const [reason, setReason] = useState("");
  const [coach, setCoach] = useState("");                                        // TWS's brief orienting words when TWS opened it (level 2) — shown beside Next
  const twsKey = useRef("");                                                     // the orientation key TWS opened for (empty when the student opened it)
  const set = (o) => { try { sessionStorage.setItem(key, o ? "1" : "0"); } catch { /* ignore */ } setOpen(o); if (!o) { setReason(""); setCoach(""); } };
  const close = () => {
    if (twsKey.current && onDismiss) { const k = twsKey.current; Promise.resolve().then(() => onDismiss(k)).catch(() => { /* presentation memory only */ }); }
    twsKey.current = "";
    set(false);
  };
  const toggle = () => (open ? close() : set(true));
  const requestAttention = (signal) => {                                        // TWS's deterministic invocation seam — a presentation action, never a developmental state
    if (!signal || Number(signal.level) !== 2) return;
    twsKey.current = signal.key || "";
    setReason(String(signal.message || signal.reason || ""));
    setCoach(String(signal.coach || ""));
    set(true);
  };
  useEffect(() => {
    if (!enabled || !sessionId || !ot) return undefined;
    let live = true;
    const t = setTimeout(() => { fetcher(sessionId).then((w) => { if (live) setWhole(w); }).catch(() => { /* orientation only: stay quiet */ }); }, 150);
    return () => { live = false; clearTimeout(t); };
  }, [sessionId, ot, enabled, fetcher]);
  return { whole, open, toggle, close, requestAttention, reason, coach };
}
